import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { decryptText, encryptText } from '../utils/utils';

interface ChatSession {
    id: string; // UI session ID
    title: string;
    timestamp: number;
    history: { query: string; apiData: any | null; loading: boolean }[];
}

interface ChatHistoryContextValue {
    sessions: ChatSession[];
    activeSessionId: string | null;
    apiSessionId: string; // API session ID (constant)
    createNewSession: () => string;
    setActiveSession: (sessionId: string) => void;
    updateSessionHistory: (sessionId: string, history: any[]) => void;
    deleteSession: (sessionId: string) => void;
    getActiveSession: () => ChatSession | null;
    resetAllSessions: () => void; // For session expiry
}

const ChatHistoryContext = createContext<ChatHistoryContextValue | undefined>(undefined);

const STORAGE_KEY = 'chat_sessions';
const API_SESSION_KEY = 'api_session_id';

export const useChatHistory = () => {
    const context = useContext(ChatHistoryContext);
    if (!context) throw new Error('useChatHistory must be used within ChatHistoryProvider');
    return context;
};

const generateSessionId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `session_${timestamp}_${random}`;
};

interface ChatHistoryProviderProps {
    children: ReactNode;
}

export const ChatHistoryProvider = ({ children }: ChatHistoryProviderProps) => {
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [apiSessionId, setApiSessionId] = useState<string>('');

    // Initialize API session ID (persistent across all chats)
    useEffect(() => {
        let storedApiSession = localStorage.getItem(API_SESSION_KEY);

        if (!storedApiSession) {
            storedApiSession = generateSessionId();
            localStorage.setItem(API_SESSION_KEY, storedApiSession);
        }

        setApiSessionId(storedApiSession);
    }, []);

    // Load UI sessions from localStorage
    useEffect(() => {
        const storedSessions = localStorage.getItem(STORAGE_KEY);
        if (storedSessions) {
            try {
                const decrypted = JSON.parse(decryptText(storedSessions));
                setSessions(decrypted);

                if (decrypted.length > 0 && !activeSessionId) {
                    setActiveSessionId(decrypted[0].id);
                }
            } catch (e) {
                console.error('Failed to parse stored sessions:', e);
                setSessions([]);
            }
        }
    }, []);

    // Save UI sessions to localStorage
    useEffect(() => {
        if (sessions.length > 0) {
            localStorage.setItem(STORAGE_KEY, encryptText(JSON.stringify(sessions)));
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [sessions]);

    const createNewSession = useCallback(() => {
        const newSessionId = generateSessionId();
        const newSession: ChatSession = {
            id: newSessionId,
            title: 'New Chat',
            timestamp: Date.now(),
            history: [], // ← Always empty
        };

        setSessions(prev => [newSession, ...prev]);
        setActiveSessionId(newSessionId);
        return newSessionId;
    }, []);

    const setActiveSession = useCallback((sessionId: string) => {
        setActiveSessionId(sessionId);
    }, []);

    const updateSessionHistory = useCallback((sessionId: string, history: any[]) => {
        setSessions(prev =>
            prev.map(session =>
                session.id === sessionId
                    ? {
                        ...session,
                        title: history.length > 0
                            ? history[0].query.substring(0, 50) + (history[0].query.length > 50 ? '...' : '')
                            : 'New Chat',
                        history,
                        timestamp: Date.now(),
                    }
                    : session
            ).sort((a, b) => b.timestamp - a.timestamp)
        );
    }, []);

    const deleteSession = useCallback((sessionId: string) => {
        // Remove session-specific localStorage
        localStorage.removeItem(`chat_history_${sessionId}`);

        setSessions(prev => {
            const filtered = prev.filter(s => s.id !== sessionId);

            // If deleting active session, switch to another
            if (activeSessionId === sessionId && filtered.length > 0) {
                setActiveSessionId(filtered[0].id);
            } else if (filtered.length === 0) {
                setActiveSessionId(null);
            }

            return filtered;
        });
    }, [activeSessionId]);

    const getActiveSession = useCallback(() => {
        return sessions.find(s => s.id === activeSessionId) || null;
    }, [sessions, activeSessionId]);

    const resetAllSessions = useCallback(() => {

        // Clear all session-related data
        sessions.forEach(session => {
            localStorage.removeItem(`chat_history_${session.id}`);
        });

        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(API_SESSION_KEY);
        localStorage.removeItem('current_session_id');

        setSessions([]);
        setActiveSessionId(null);

        // Generate new API session ID
        const newApiSession = generateSessionId();
        localStorage.setItem(API_SESSION_KEY, newApiSession);
        setApiSessionId(newApiSession);
    }, [sessions]);

    return (
        <ChatHistoryContext.Provider
            value={{
                sessions,
                activeSessionId,
                apiSessionId,
                createNewSession,
                setActiveSession,
                updateSessionHistory,
                deleteSession,
                getActiveSession,
                resetAllSessions,
            }}
        >
            {children}
        </ChatHistoryContext.Provider>
    );
};